/**
 * Component: EventsTable
 * Displays list of events in a table format
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Eye, Edit, Trash2 } from 'lucide-react';
import type { Event, EventStatus } from '@/types/event.types';
import type { EventWithRelations } from '../types';
import {
  formatDateRange,
  formatPrice,
  getStatusLabel,
  getStatusBadgeVariant,
} from '@/models/event';
import { InlineLoader } from '@/components/ui/pulsing-loader';

interface EventsTableProps {
  events: EventWithRelations[];
  isLoading: boolean;
  canEdit: boolean;
  onView: (event: Event) => void;
  onEdit: (event: Event) => void;
  onDelete: (event: Event) => void;
}

export function EventsTable({
  events,
  isLoading,
  canEdit,
  onView,
  onEdit,
  onDelete,
}: EventsTableProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <InlineLoader message="Cargando eventos..." />
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">No se encontraron eventos.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Código</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Fechas</TableHead>
            <TableHead>Ubicación</TableHead>
            <TableHead>Precio base</TableHead>
            <TableHead className="text-right">Inscritos</TableHead>
            <TableHead className="text-right">Recaudado</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {events.map((event) => (
            <TableRow
              key={event.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onView(event)}
            >
              {/* Code */}
              <TableCell className="font-medium">{event.code}</TableCell>

              {/* Name */}
              <TableCell>
                <div className="max-w-[200px]">
                  <div className="truncate font-medium">{event.name}</div>
                  {event.description && (
                    <div className="truncate text-sm text-muted-foreground">
                      {event.description}
                    </div>
                  )}
                </div>
              </TableCell>

              {/* Dates */}
              <TableCell>{formatDateRange(event)}</TableCell>

              {/* Location */}
              <TableCell>
                <div className="max-w-[150px] truncate">
                  {event.location || '-'}
                </div>
              </TableCell>

              {/* Base Price */}
              <TableCell>{formatPrice(event.base_price || 0)}</TableCell>

              {/* Participants / revenue */}
              <TableCell className="text-right">
                {event.participants_count ?? 0}
                {(event.pending_review_count ?? 0) > 0 && (
                  <span className="block text-xs text-amber-700">{event.pending_review_count} por revisar</span>
                )}
              </TableCell>
              <TableCell className="text-right">{formatPrice(event.revenue ?? 0)}</TableCell>

              {/* Status */}
              <TableCell>
                <Badge
                  variant={getStatusBadgeVariant(event.status as EventStatus)}
                >
                  {getStatusLabel(event.status as EventStatus)}
                </Badge>
              </TableCell>

              {/* Actions */}
              <TableCell
                className="text-right"
                onClick={(e) => e.stopPropagation()}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView(event)}>
                      <Eye className="mr-2 size-4" />
                      Ver Detalles
                    </DropdownMenuItem>
                    {canEdit && (
                      <>
                        <DropdownMenuItem onClick={() => onEdit(event)}>
                          <Edit className="mr-2 size-4" />
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete(event)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 size-4" />
                          Eliminar
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
